import React, { useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  BookOpen,
  Lock,
  ChevronDown,
  ChevronRight,
  IndentIncrease,
} from "lucide-react";

// V1.24 · Nodo recursivo + bloque contenedor del editor jerárquico.
// NOTA técnica: la auto-referencia recursiva de `ClauseNode` se hace vía
// `React.createElement` en lugar de JSX porque el babel-loader de CRA sufre
// un stack-overflow (RangeError) al analizar componentes JSX que se
// referencian a sí mismos directamente. `createElement` bypassa el bug
// preservando exactamente la misma semántica.

// -------------------- Immutable tree helpers ----------------------------
export function mapAtPath(tree, path, updater) {
  if (path.length === 0) return updater(tree);
  const [head, ...rest] = path;
  return tree.map((n, i) => {
    if (i !== head) return n;
    return { ...n, children: mapAtPath(n.children || [], rest, updater) };
  });
}
export function updateNode(tree, path, patch) {
  if (path.length === 0) return tree;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return mapAtPath(tree, parentPath, (siblings) =>
    siblings.map((n, i) => (i === idx ? { ...n, ...patch } : n))
  );
}
export function deleteNode(tree, path) {
  if (path.length === 0) return tree;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return mapAtPath(tree, parentPath, (siblings) =>
    siblings.filter((_, i) => i !== idx)
  );
}
export function addChildAt(tree, path, newNode) {
  const walk = (nodes, i) => {
    if (i === path.length) return [...nodes, newNode];
    return nodes.map((n, idx) => {
      if (idx !== path[i]) return n;
      const kids = n.children || [];
      if (i === path.length - 1) return { ...n, children: [...kids, newNode] };
      return { ...n, children: walk(kids, i + 1) };
    });
  };
  return walk(tree, 0);
}
export function addSiblingAfter(tree, path, newNode) {
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return mapAtPath(tree, parentPath, (siblings) => {
    const out = [...siblings];
    out.splice(idx + 1, 0, newNode);
    return out;
  });
}
export function moveNode(tree, path, delta) {
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return mapAtPath(tree, parentPath, (siblings) => {
    const j = idx + delta;
    if (j < 0 || j >= siblings.length) return siblings;
    const out = [...siblings];
    [out[idx], out[j]] = [out[j], out[idx]];
    return out;
  });
}
export function emptyClause() {
  return { title: "", body: "", children: [] };
}
function countAll(arr) {
  return (arr || []).reduce((acc, n) => acc + 1 + countAll(n.children), 0);
}

// -------------------- Toolbar of a clause (extracted to reduce complexity)
function ClauseToolbar({ path, number, node, canMoveUp, canMoveDown, disabled, onTree }) {
  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        disabled={disabled || !canMoveUp}
        onClick={() => onTree((t) => moveNode(t, path, -1))}
        className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
        title="Subir"
        data-testid={"clause-up-" + number}
      ><ArrowUp className="h-3.5 w-3.5" /></button>
      <button
        type="button"
        disabled={disabled || !canMoveDown}
        onClick={() => onTree((t) => moveNode(t, path, 1))}
        className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
        title="Bajar"
        data-testid={"clause-down-" + number}
      ><ArrowDown className="h-3.5 w-3.5" /></button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onTree((t) => addSiblingAfter(t, path, emptyClause()))}
        className="p-1 rounded hover:bg-blue-50 text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed"
        title="Añadir cláusula hermana debajo"
        data-testid={"clause-add-sibling-" + number}
      ><Plus className="h-3.5 w-3.5" /></button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onTree((t) => addChildAt(t, path, emptyClause()))}
        className="p-1 rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed"
        title="Añadir subcláusula (hija)"
        data-testid={"clause-add-child-" + number}
      ><IndentIncrease className="h-3.5 w-3.5" /></button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          const kids = node.children || [];
          if (kids.length && !window.confirm("Esta cláusula tiene " + kids.length + " subcláusula(s) que también se eliminarán. ¿Continuar?")) return;
          onTree((t) => deleteNode(t, path));
        }}
        className="p-1 rounded hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
        title="Eliminar (incluye subcláusulas)"
        data-testid={"clause-delete-" + number}
      ><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// -------------------- Recursive clause node ----------------------------
export function ClauseNode({ node, path, blockClauses, disabled, onTree }) {
  const number = path.map((n) => n + 1).join(".");
  const depth = path.length - 1;
  const marginLeftPx = Math.min(depth * 20, 200);
  const canMoveUp = path[path.length - 1] > 0;
  let siblingList = blockClauses;
  for (let i = 0; i < path.length - 1; i++) siblingList = siblingList[path[i]].children || [];
  const canMoveDown = path[path.length - 1] < siblingList.length - 1;

  const patchNode = (fields) => onTree((t) => updateNode(t, path, fields));
  const numBg = depth === 0 ? "bg-orange-100 text-orange-800" : "bg-neutral-100 text-neutral-700";
  const cardBorder = depth === 0 ? "border-neutral-300 shadow-sm" : "border-neutral-200";

  const childrenElements = (node.children || []).map((child, ci) =>
    React.createElement(ClauseNode, {
      key: ci,
      node: child,
      path: [...path, ci],
      blockClauses: blockClauses,
      disabled: disabled,
      onTree: onTree,
    })
  );

  return (
    <div style={{ marginLeft: marginLeftPx }} data-testid={"clause-node-" + number}>
      <div className={"rounded-lg border bg-white p-3 space-y-2 mb-2 " + cardBorder}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs">
            <span className={"inline-flex items-center rounded font-mono font-bold px-1.5 py-0.5 " + numBg}>
              {number}.
            </span>
            <span className="text-[10px] font-semibold text-neutral-500 uppercase">
              Nivel {depth + 1}
            </span>
            {disabled ? <Lock className="h-3 w-3 text-neutral-400" /> : null}
          </div>
          <ClauseToolbar
            path={path}
            number={number}
            node={node}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            disabled={disabled}
            onTree={onTree}
          />
        </div>
        <textarea
          value={node.body || ""}
          onChange={(e) => patchNode({ body: e.target.value })}
          disabled={disabled}
          rows={Math.min(8, Math.max(2, (node.body || "").split("\n").length + 1))}
          placeholder="Texto verbatim de la cláusula. El sistema NO reformula ni corrige nada."
          className="w-full rounded-md border border-neutral-300 px-2.5 py-2 text-sm font-serif leading-relaxed disabled:bg-neutral-50 disabled:text-neutral-600"
          data-testid={"clause-body-" + number}
        />
      </div>
      {childrenElements}
    </div>
  );
}

// -------------------- Block section (collapsible) ----------------------
export function BlockSection({ block, clauses, disabled, onSetClauses, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const totalCount = useMemo(() => countAll(clauses), [clauses]);
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 overflow-hidden" data-testid={"block-section-" + block.key}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-neutral-100 transition-colors"
        data-testid={"block-toggle-" + block.key}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <BookOpen className="h-4 w-4 text-orange-500" />
          <span className="font-semibold text-sm">{block.label}</span>
          <span className="rounded-full bg-neutral-200 text-neutral-700 text-[10px] font-mono px-1.5 py-0.5">
            {totalCount}
          </span>
        </div>
        {disabled ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-neutral-500">
            <Lock className="h-3 w-3" /> Solo lectura
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="p-3 border-t border-neutral-200 bg-white">
          {clauses.length === 0 ? (
            <p className="text-xs text-neutral-500 italic px-2 py-3">
              Sin cláusulas registradas. Añade la primera.
            </p>
          ) : null}
          {clauses.map((c, i) => (
            <ClauseNode
              key={i}
              node={c}
              path={[i]}
              blockClauses={clauses}
              disabled={disabled}
              onTree={(updater) => onSetClauses(updater(clauses))}
            />
          ))}
          {!disabled ? (
            <button
              type="button"
              onClick={() => onSetClauses([...(clauses || []), emptyClause()])}
              className="mt-1 inline-flex items-center gap-1 rounded-lg border border-dashed border-orange-400 bg-orange-50 hover:bg-orange-100 text-orange-800 px-3 py-1.5 text-xs font-semibold transition-colors"
              data-testid={"add-root-clause-" + block.key}
            >
              <Plus className="h-3.5 w-3.5" /> Añadir cláusula raíz
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
